import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {FmlxDropdown} from 'fmlx-common-ui';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import withIntl from '../../../lib/withIntl';
import '../SetupWizard.scss';
import {getTargetMails, setAutomatedErrorReport} from '../../../stores/business/instrumentBusiness';
import {setWizardAllowedNext} from '../../../stores/actions/wizardStateAction';
import {SetupWizardStep} from '../../../constants/SetupWizardEnum';
import {handleDefaultWizardAPICallResponse} from '../../../stores/business/setupWizardBusiness/setupWizardInstrumentBusiness';

const mapStateToProps = (state) => ({
  stepNameState: state.wizardState.stepName,
});

const mapDispatchToProps = (dispatch) => ({
  instrumentBusinessGetTargetMail: bindActionCreators(getTargetMails, dispatch),
  wizardStateActionSetAllowedNext: bindActionCreators(setWizardAllowedNext, dispatch),
  instrumentBusinessSetAutomatedErrorReport: bindActionCreators(setAutomatedErrorReport, dispatch),
  SWInstrumentBusinessHandleDefaultWizardAPICallResponse: bindActionCreators(
    handleDefaultWizardAPICallResponse,
    dispatch,
  ),
});

const StepOneSetupWizard = forwardRef(
  (
    {
      intl,
      wizardStateActionSetAllowedNext,
      instrumentBusinessGetTargetMail,
      instrumentBusinessSetAutomatedErrorReport,
      onSaveStepData,
      SWInstrumentBusinessHandleDefaultWizardAPICallResponse,
      stepNameState,
      stepData,
    },
    ref,
  ) => {
    const [targetMailOptions, setTargetMailOptions] = useState([]);
    const [instrumentAlias, setInstrumentAlias] = useState('');
    const [targetMailIndex, setTargetMailIndex] = useState(0);

    const getTargetMailString = () => targetMailOptions.find(({id}) => id === targetMailIndex).value;

    const fetchTargetMail = async () => {
      const fetchResult = await instrumentBusinessGetTargetMail();
      const targetMailResult = [];
      fetchResult.forEach((val, index) => {
        targetMailResult.push({
          id: index,
          value: val.mail,
          text: `${val.target} - ${val.mail}`,
        });
      });
      setTargetMailOptions(targetMailResult);
    };

    const checkIfStepValid = () => {
      if (stepNameState !== SetupWizardStep.AUTOMATED_ERROR_REPORT) return;

      if (instrumentAlias !== '' && targetMailIndex >= 0 && targetMailOptions.length > 0) {
        const targetMailString = getTargetMailString();
        onSaveStepData({instrumentAlias, targetMailIndex, targetMail: targetMailString});
        wizardStateActionSetAllowedNext(true);
      } else {
        wizardStateActionSetAllowedNext(false);
      }
    };

    const onContinue = async (skipStep) => {
      if (!skipStep) {
        const targetMail = getTargetMailString();
        const result = await instrumentBusinessSetAutomatedErrorReport(instrumentAlias, targetMail);

        await SWInstrumentBusinessHandleDefaultWizardAPICallResponse(result);
      } else {
        onSaveStepData({instrumentAlias: '', targetMail: '', targetMailIndex: -1});
      }
    };

    useImperativeHandle(ref, () => ({
      onContinue,
    }));

    useEffect(() => {
      fetchTargetMail();

      if (stepData) {
        setInstrumentAlias(stepData.instrumentAlias);
        setTargetMailIndex(stepData.targetMailIndex);
        wizardStateActionSetAllowedNext(true);
      }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      checkIfStepValid();
    }, [instrumentAlias, targetMailIndex, targetMailOptions]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <div className="step-automated-error-report" ref={ref}>
        <div className="input-container align-left">
          {intl.formatMessage({id: 'setupWizard.step.automatedErrorReport.firstInput.title'})}
          <br />
          <input
            value={instrumentAlias}
            onChange={(event) => {
              setInstrumentAlias(event.target.value);
            }}
            className="alias-input"
            placeholder={intl.formatMessage({id: 'setupWizard.step.automatedErrorReport.firstInput.placeholder'})}
          />
        </div>
        <div className="input-container align-left">
          {intl.formatMessage({id: 'setupWizard.step.automatedErrorReport.secondInput.title'})}
          <br />
          <FmlxDropdown
            items={targetMailOptions}
            selectedItem={targetMailIndex}
            onChange={(idx) => setTargetMailIndex(idx)}
            style={{width: '42vw'}}
            placeholder={intl.formatMessage({id: 'setupWizard.step.automatedErrorReport.secondInput.placeholder'})}
          />
        </div>
      </div>
    );
  },
);

StepOneSetupWizard.propTypes = {
  wizardStateActionSetAllowedNext: PropTypes.func.isRequired,
  instrumentBusinessGetTargetMail: PropTypes.func.isRequired,
  instrumentBusinessSetAutomatedErrorReport: PropTypes.func.isRequired,
  onSaveStepData: PropTypes.func.isRequired,
  SWInstrumentBusinessHandleDefaultWizardAPICallResponse: PropTypes.func.isRequired,
  stepNameState: PropTypes.number.isRequired,
  stepData: PropTypes.object.isRequired,
  intl: PropTypes.any.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(withIntl(StepOneSetupWizard));
